// Matching for the settings search box (settings_search_controller.js). Entries come from
// Settings::SearchIndex: { title, tab, group, keywords, url }, all texts already translated.

// Umlauts and their spelled-out forms match each other and the bare vowel, so "Uberstunden",
// "Ueberstunden" and "Überstunden" all find the same card. Every replacement keeps or
// shortens the text; foldWithMap records where each folded character came from so the
// highlight can be drawn on the original text.
const FOLDS = [
  ["ae", "a"],
  ["oe", "o"],
  ["ue", "u"],
  ["ä", "a"],
  ["ö", "o"],
  ["ü", "u"],
  ["ß", "ss"],
]

export function foldWithMap(text) {
  const lower = text.toLocaleLowerCase("de")
  let folded = ""
  const map = []
  for (let i = 0; i < lower.length;) {
    const pair = FOLDS.find(([from]) => lower.startsWith(from, i))
    const [from, to] = pair || [lower[i], lower[i]]
    for (let k = 0; k < to.length; k++) map.push(i)
    folded += to
    i += from.length
  }
  return { folded, map }
}

export function fold(text) {
  return foldWithMap(text).folded
}

export function queryWords(query) {
  return fold(query).split(/\s+/).filter(Boolean)
}

const wordStarts = (text, word) => text.split(/[^a-z0-9§²³]+/).some((part) => part.startsWith(word))

// The word of a keyword the query hit, so a sentence-long keyword (an integration's
// tagline) is named by that one word in the "gefunden über" hint. Short keywords stay whole.
function matchedWord(keyword, word) {
  if (!keyword.includes(" ")) return keyword
  const words = keyword.split(/[\s,.;:()/]+/).filter(Boolean)
  return words.find((part) => fold(part).includes(word)) || keyword
}

// Score of one query word against one entry; 0 means no match. Title beats keyword beats
// where the card sits, so "Pause" lists "Pausenzeit" before cards that only mention pauses.
function scoreWord(entry, word) {
  if (entry.foldedTitle.startsWith(word)) return { score: 100 }
  if (wordStarts(entry.foldedTitle, word)) return { score: 80 }
  if (entry.foldedTitle.includes(word)) return { score: 60 }
  const keyword = entry.foldedKeywords.findIndex((kw) => wordStarts(kw, word))
  if (keyword >= 0) return { score: 50, via: matchedWord(entry.keywordList[keyword], word) }
  const inside = entry.foldedKeywords.findIndex((kw) => kw.includes(word))
  if (inside >= 0) return { score: 40, via: matchedWord(entry.keywordList[inside], word) }
  if (entry.foldedPlace.includes(word)) return { score: 30 }
  return { score: 0 }
}

// Folds every entry once, so typing only folds the query.
export function prepare(entries) {
  return entries.map((entry) => {
    const keywordList = (entry.keywords || "")
      .split(",")
      .map((kw) => kw.trim())
      .filter(Boolean)
    return {
      ...entry,
      foldedTitle: fold(entry.title),
      foldedPlace: fold([entry.tab, entry.group].filter(Boolean).join(" ")),
      keywordList,
      foldedKeywords: keywordList.map(fold),
    }
  })
}

// Every word must match somewhere. Returns [{ entry, score, via }], best first; via is the
// keyword that matched when the title alone did not.
export function search(prepared, query) {
  const words = queryWords(query)
  if (words.length === 0) return []

  const hits = []
  prepared.forEach((entry) => {
    let score = 0
    let via = null
    let titleMatched = false
    for (const word of words) {
      const result = scoreWord(entry, word)
      if (result.score === 0) return
      score += result.score
      if (result.score >= 60) titleMatched = true
      via ||= result.via || null
    }
    hits.push({ entry, score, via: titleMatched ? null : via })
  })
  return hits.sort((a, b) => b.score - a.score || a.entry.title.localeCompare(b.entry.title, "de"))
}

// Results grouped by tab, keeping the rank order of each tab's best hit.
export function groupByTab(hits, limitPerTab = 6) {
  const groups = new Map()
  hits.forEach((hit) => {
    if (!groups.has(hit.entry.tab)) groups.set(hit.entry.tab, [])
    const list = groups.get(hit.entry.tab)
    if (list.length < limitPerTab) list.push(hit)
  })
  return [...groups].map(([tab, items]) => ({ tab, items }))
}

// [start, end) ranges of +text+ (original indices) that the query words match, merged.
export function highlightRanges(text, query) {
  const { folded, map } = foldWithMap(text)
  const ranges = []
  queryWords(query).forEach((word) => {
    const at = folded.indexOf(word)
    if (at < 0) return
    const next = at + word.length
    // Up to where the next folded character starts, so a folded "ae" is marked whole; at
    // least past the last matched source character ("ß" folds to two characters).
    const end = Math.max(next < map.length ? map[next] : text.length, map[next - 1] + 1)
    ranges.push([map[at], end])
  })
  ranges.sort((a, b) => a[0] - b[0])
  return ranges.reduce((merged, range) => {
    const last = merged[merged.length - 1]
    if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1])
    else merged.push([...range])
    return merged
  }, [])
}
